import {
  createUser,
  fetchAdminAccounts,
  fetchAvailability,
  fetchFormOptions,
} from './createUserService';

const ok = (body) =>
  jest.fn().mockResolvedValue({ ok: true, json: async () => body });

afterEach(() => {
  delete global.fetch;
  document.head.innerHTML = '';
});

describe('management/Users createUserService', () => {
  describe('fetchFormOptions', () => {
    it('asks the form_options endpoint', async () => {
      global.fetch = ok({ jobRoles: [] });

      await fetchFormOptions();

      expect(global.fetch).toHaveBeenCalledWith(
        '/management/users/form_options.json',
        expect.anything(),
      );
    });

    it('throws on a failure', async () => {
      global.fetch = jest.fn().mockResolvedValue({ ok: false, status: 500 });

      await expect(fetchFormOptions()).rejects.toThrow('500');
    });
  });

  describe('fetchAdminAccounts', () => {
    it('returns the page and the matching total', async () => {
      global.fetch = ok({
        accounts: [{ label: 'Alpha', value: 7 }],
        total: 90,
      });

      await expect(fetchAdminAccounts()).resolves.toEqual({
        accounts: [{ label: 'Alpha', value: 7 }],
        total: 90,
      });
    });

    it('sends no query string when there is nothing to search for', async () => {
      global.fetch = ok({ accounts: [], total: 0 });

      await fetchAdminAccounts();

      expect(global.fetch.mock.calls[0][0]).toEqual(
        '/management/users/admin_accounts.json',
      );
    });

    it('passes the search term', async () => {
      global.fetch = ok({ accounts: [], total: 0 });

      await fetchAdminAccounts('alpha fleet');

      expect(global.fetch.mock.calls[0][0]).toContain('q=alpha+fleet');
    });

    it('asks for the page at an offset', async () => {
      global.fetch = ok({ accounts: [], total: 0 });

      await fetchAdminAccounts('', 50);

      expect(global.fetch.mock.calls[0][0]).toContain('offset=50');
    });

    // The first page is offset 0, which needs no parameter.
    it('omits a zero offset', async () => {
      global.fetch = ok({ accounts: [], total: 0 });

      await fetchAdminAccounts();

      expect(global.fetch.mock.calls[0][0]).not.toContain('offset');
    });

    // Reporting 0 would read as "no accounts" and hide the ones that arrived.
    it('falls back to the page length when a response carries no total', async () => {
      global.fetch = ok({ accounts: [{ label: 'Alpha', value: 7 }] });

      await expect(fetchAdminAccounts()).resolves.toEqual({
        accounts: [{ label: 'Alpha', value: 7 }],
        total: 1,
      });
    });
  });

  describe('fetchAvailability', () => {
    it('reports what is taken', async () => {
      global.fetch = ok({
        emailTaken: true,
        mobileTaken: false,
        mobileValid: true,
      });

      await expect(
        fetchAvailability({ email: 'taken@example.com' }),
      ).resolves.toEqual({
        emailTaken: true,
        mobileTaken: false,
        mobileValid: true,
      });
    });

    it('sends only the fields it was given', async () => {
      global.fetch = ok({
        emailTaken: false,
        mobileTaken: false,
        mobileValid: true,
      });

      await fetchAvailability({ email: 'jane@example.com' });

      const url = global.fetch.mock.calls[0][0];
      expect(url).toContain('email=jane%40example.com');
      expect(url).not.toContain('mobile');
    });

    it('sends both halves of a mobile number', async () => {
      global.fetch = ok({
        emailTaken: false,
        mobileTaken: false,
        mobileValid: true,
      });

      await fetchAvailability({
        mobile: '2024561111',
        mobileCountryCode: '52',
      });

      const url = global.fetch.mock.calls[0][0];
      expect(url).toContain('mobile=2024561111');
      expect(url).toContain('mobile_country_code=52');
    });
  });

  describe('createUser', () => {
    const payload = { user: { email: 'jane@example.com' }, account_ids: ['7'] };

    it('posts the payload and returns the created user', async () => {
      global.fetch = ok({ user: { id: 1, username: 'jane' } });

      await expect(createUser(payload)).resolves.toEqual({
        id: 1,
        username: 'jane',
      });

      const [url, options] = global.fetch.mock.calls[0];
      expect(url).toEqual('/management/users.json');
      expect(options.method).toEqual('POST');
      expect(JSON.parse(options.body)).toEqual(payload);
    });

    // Rails rejects the post without it.
    it('sends the CSRF token from the page', async () => {
      document.head.innerHTML =
        '<meta name="csrf-token" content="a-real-token" />';
      global.fetch = ok({ user: {} });

      await createUser(payload);

      expect(global.fetch.mock.calls[0][1].headers['X-CSRF-Token']).toEqual(
        'a-real-token',
      );
    });

    it('sends an empty token rather than crashing when the meta tag is absent', async () => {
      global.fetch = ok({ user: {} });

      await createUser(payload);

      expect(global.fetch.mock.calls[0][1].headers['X-CSRF-Token']).toEqual('');
    });

    // A 422 is the model's own errors, which the wizard may want to point at.
    it('carries the field errors on a 422', async () => {
      global.fetch = jest.fn().mockResolvedValue({
        ok: false,
        status: 422,
        json: async () => ({ errors: { email: ['has already been taken'] } }),
      });

      await expect(createUser(payload)).rejects.toMatchObject({
        fieldErrors: { email: ['has already been taken'] },
      });
    });

    it('does not let a malformed 422 body mask the failure', async () => {
      global.fetch = jest.fn().mockResolvedValue({
        ok: false,
        status: 422,
        json: async () => {
          throw new Error('not json');
        },
      });

      await expect(createUser(payload)).rejects.toMatchObject({
        fieldErrors: {},
      });
    });

    it('throws bare on any other failure', async () => {
      global.fetch = jest.fn().mockResolvedValue({ ok: false, status: 500 });

      await expect(createUser(payload)).rejects.toThrow('500');
    });
  });
});
